import * as z from "zod";

/**
 * Surfaces the state of the HubSpot mirror ingest pipeline and lifecycle
 * runtime. Powers the admin Sync tab: "last sync, row counts, schedule
 * existence, trigger a run". Read-only.
 */
export const getHubspotSyncHealthInputSchema = z.object({});
export type GetHubspotSyncHealthInput = z.infer<
  typeof getHubspotSyncHealthInputSchema
>;

const bookmarkSchema = z.object({
  kind: z.string(),
  cursorAt: z.date(),
  lastDealCursor: z.string().nullable(),
  updatedAt: z.date(),
});

const scheduleExistenceSchema = z.object({
  scheduleId: z.string(),
  exists: z.boolean(),
  /** Optional error message if the describe call failed for a non-not-found reason. */
  describeError: z.string().nullable(),
});

const rowCountsSchema = z.object({
  hubspotDeals: z.number().int().min(0),
  hubspotDealsLinked: z.number().int().min(0),
  hubspotPipelines: z.number().int().min(0),
  hubspotStages: z.number().int().min(0),
  hubspotOwners: z.number().int().min(0),
  lifecycleEvents: z.number().int().min(0),
  progressRows: z.number().int().min(0),
  progressWithHubspotDealId: z.number().int().min(0),
  progressWithFirstRevenue: z.number().int().min(0),
  progressRevenueGenerating: z.number().int().min(0),
  progressChurned: z.number().int().min(0),
});
export type HubspotSyncRowCounts = z.infer<typeof rowCountsSchema>;

const deltaSchema = z.object({
  kind: z.string(),
  /** Most recent event timestamp. Null if we have no events yet. */
  mostRecentEventAt: z.date().nullable(),
  /** Events inserted in the last 24h. */
  last24h: z.number().int().min(0),
  /** Events inserted in the last 7d. */
  last7d: z.number().int().min(0),
});
export type HubspotSyncDelta = z.infer<typeof deltaSchema>;

/**
 * Tells the admin what `since` date the next nightly mirror will use.
 * Computed the same way the workflow does — bookmark first, then
 * onboarding-floor, then 24h fallback — so the UI can say "the next
 * run will pull Returns deals modified since {date}."
 */
const mirrorNextRunSchema = z.object({
  source: z.enum(["bookmark", "onboarding_floor", "fallback_24h"]),
  since: z.date(),
  onboardingFloor: z.date().nullable(),
  bookmarkAt: z.date().nullable(),
});
export type MirrorNextRunPreview = z.infer<typeof mirrorNextRunSchema>;

/**
 * Distinct `product_being_sold_revenue` values found in the mirror,
 * grouped by deal count. Diagnostic: if the filter only lets "Returns"
 * through but HubSpot actually stores "returns" or "Returns (coverage)"
 * or similar, the mirror will look healthy but analytics empty.
 */
const productValueBreakdownSchema = z.object({
  productValue: z.string(),
  dealCount: z.number().int().min(0),
});
export type HubspotProductValueBreakdown = z.infer<
  typeof productValueBreakdownSchema
>;

/** Top pipelines in the mirror joined to deal counts. Diagnostic: if
 * the "Returns" pipeline has zero deals after a sync, the mirror
 * fetched deals but they landed in the wrong pipeline bucket. */
const pipelineBreakdownSchema = z.object({
  pipelineId: z.string().nullable(),
  label: z.string().nullable(),
  dealCount: z.number().int().min(0),
});
export type HubspotPipelineBreakdown = z.infer<typeof pipelineBreakdownSchema>;

/** Top stages (across all pipelines) with the deal count and our
 * normalization tag. Diagnostic: analytics filter on `stage_normalized`
 * (closed_won etc.); if HubSpot has a stage whose normalization came
 * back NULL, those deals are silently excluded from analytics. */
const stageBreakdownSchema = z.object({
  stageId: z.string().nullable(),
  label: z.string().nullable(),
  pipelineLabel: z.string().nullable(),
  stageNormalized: z.string().nullable(),
  dealCount: z.number().int().min(0),
});
export type HubspotStageBreakdown = z.infer<typeof stageBreakdownSchema>;

/** Why a Returns progress row might drop out of the cohort view. */
const progressBreakdownSchema = z.object({
  totalReturns: z.number().int().min(0),
  nullStartedAt: z.number().int().min(0),
  archived: z.number().int().min(0),
  nullTeamId: z.number().int().min(0),
  nullHubspotDealId: z.number().int().min(0),
});
export type ReturnsProgressBreakdown = z.infer<typeof progressBreakdownSchema>;

export const getHubspotSyncHealthOutputSchema = z.object({
  bookmarks: z.array(bookmarkSchema),
  schedules: z.array(scheduleExistenceSchema),
  rowCounts: rowCountsSchema,
  lifecycleEventActivity: z.array(deltaSchema),
  /** When the `revenue_checked_at` column was last touched (proxy for lifecycle recompute activity). */
  lifecycleRecomputeLastRunAt: z.date().nullable(),
  mirrorNextRun: mirrorNextRunSchema,
  productValueBreakdown: z.array(productValueBreakdownSchema),
  pipelineBreakdown: z.array(pipelineBreakdownSchema),
  stageBreakdown: z.array(stageBreakdownSchema),
  progressBreakdown: progressBreakdownSchema,
});
export type GetHubspotSyncHealthOutput = z.infer<
  typeof getHubspotSyncHealthOutputSchema
>;

export const getHubspotSyncHealth = {
  name: "getHubspotSyncHealth" as const,
  input: getHubspotSyncHealthInputSchema,
  output: getHubspotSyncHealthOutputSchema,
};
